/**
 * Feature Management Service
 * 
 * Provides utilities for checking and managing tenant feature access.
 */

import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import {
  UPDATE_TENANT_FEATURES,
  GET_TENANT_FEATURES,
  CHECK_TENANT_FEATURE_ACCESS,
  GET_CURRENT_TENANT_FEATURES,
} from '../graphql/admin';
import {
  TenantFeature,
  UpdateTenantFeaturesInput,
  FeatureAccessResponse,
  TenantFeaturesResponse,
  Tenant,
} from '../types/admin';

const client = generateClient();

/**
 * Feature display information
 */
export interface FeatureInfo {
  key: TenantFeature;
  name: string;
  description: string;
}

/**
 * Available features with display information
 */
export const AVAILABLE_FEATURES: FeatureInfo[] = [
  {
    key: TenantFeature.DUE_DILIGENCE_ANALYSIS,
    name: 'Due Diligence Analysis',
    description: 'Review and verify loan documents for due diligence',
  },
  {
    key: TenantFeature.BORROWER_ANALYSIS,
    name: 'Borrower Analysis',
    description: 'Analyze borrower documents for underwriting',
  },
];

/**
 * Get feature display information
 */
export function getFeatureInfo(feature: string): FeatureInfo | undefined {
  return AVAILABLE_FEATURES.find((f) => f.key === feature);
}

/**
 * Update enabled features for a tenant (Platform Admin only)
 */
export async function updateTenantFeatures(
  tenantId: string,
  enabledFeatures: string[]
): Promise<Tenant> {
  const input: UpdateTenantFeaturesInput = {
    enabledFeatures,
  };

  console.log('updateTenantFeatures called with:', { tenantId, input });

  try {
    const response = await client.graphql({
      query: UPDATE_TENANT_FEATURES,
      variables: { tenantId, input }
    }) as GraphQLResult<{ updateTenantFeatures: Tenant }>;

    console.log('GraphQL response:', response);

    if (!response.data?.updateTenantFeatures) {
      console.error('No data in response:', response);
      throw new Error('Failed to update tenant features');
    }

    return response.data.updateTenantFeatures;
  } catch (error) {
    console.error('updateTenantFeatures error:', error);
    throw error;
  }
}

/**
 * Get enabled features for a specific tenant (Platform Admin only)
 */
export async function getTenantFeatures(
  tenantId: string
): Promise<TenantFeaturesResponse> {
  const response = await client.graphql({
    query: GET_TENANT_FEATURES,
    variables: { tenantId }
  }) as GraphQLResult<{ getTenantFeatures: TenantFeaturesResponse }>;

  if (!response.data?.getTenantFeatures) {
    throw new Error('Failed to get tenant features');
  }

  return response.data.getTenantFeatures;
}

/**
 * Check if current tenant has access to a specific feature
 */
export async function checkFeatureAccess(
  feature: string
): Promise<FeatureAccessResponse> {
  const response = await client.graphql({
    query: CHECK_TENANT_FEATURE_ACCESS,
    variables: { feature }
  }) as GraphQLResult<{ checkTenantFeatureAccess: FeatureAccessResponse }>;

  if (!response.data?.checkTenantFeatureAccess) {
    throw new Error('Failed to check feature access');
  }

  return response.data.checkTenantFeatureAccess;
}

/**
 * Check if a feature is enabled in a list of features
 */
export function isFeatureEnabled(
  enabledFeatures: string[],
  feature: TenantFeature
): boolean {
  return enabledFeatures.includes(feature);
}

/**
 * Get display names for enabled features
 */
export function getEnabledFeatureNames(enabledFeatures: string[]): string[] {
  return enabledFeatures
    .map((feature) => {
      const info = getFeatureInfo(feature);
      return info ? info.name : feature;
    })
    .filter(Boolean);
}

/**
 * Feature gate hook for conditional rendering
 * Returns true if the feature is enabled, false otherwise
 */
export async function useFeatureGate(feature: TenantFeature): Promise<boolean> {
  try {
    const response = await checkFeatureAccess(feature);
    return response.hasAccess;
  } catch (error) {
    console.error('Feature access check failed:', error);
    return false;
  }
}
